"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.confirmHousingLinkSchema = exports.housingPendingLinkDtoSchema = exports.housingSyncRunDtoSchema = exports.housingPlacementDtoSchema = exports.updateHousingLocationSchema = exports.housingLocationDtoSchema = exports.housingColumnMapSchema = void 0;
const zod_1 = require("zod");
// ─── HousingLocation ──────────────────────────────────────────────────────
// One per bedboard tab. `code` is the join key (verbatim tab name, including
// any trailing whitespace — Buckeye's "Mansfield-Males " has one).
//
// columnMap is open-shape JSON: { name: "C", room: "A", phone: "D", ... }
// rather than strongly typed at the schema level, because tabs are inherently
// freeform and we want the parser to tolerate missing columns gracefully.
exports.housingColumnMapSchema = zod_1.z
    .object({
    name: zod_1.z.string().optional(),
    room: zod_1.z.string().optional(),
    phone: zod_1.z.string().optional(),
    moveIn: zod_1.z.string().optional(),
    moveOut: zod_1.z.string().optional(),
    program: zod_1.z.string().optional(),
    notes: zod_1.z.string().optional(),
    restriction: zod_1.z.string().optional(),
    locker: zod_1.z.string().optional(),
    workStatus: zod_1.z.string().optional(),
    talbotId: zod_1.z.string().optional(),
})
    .passthrough();
exports.housingLocationDtoSchema = zod_1.z.object({
    id: zod_1.z.string(),
    code: zod_1.z.string(),
    displayName: zod_1.z.string(),
    address: zod_1.z.string(),
    city: zod_1.z.string(),
    state: zod_1.z.string(),
    zip: zod_1.z.string(),
    houseManagerName: zod_1.z.string(),
    houseManagerPhone: zod_1.z.string(),
    wifiName: zod_1.z.string(),
    wifiPassword: zod_1.z.string(),
    doorCode: zod_1.z.string(),
    secondaryDoorCode: zod_1.z.string(),
    columnMap: exports.housingColumnMapSchema,
    columnMapSecondary: exports.housingColumnMapSchema.nullable(),
    dataStartRow: zod_1.z.number().int().positive(),
    // Bed counts maintained by HousingSyncService on every run.
    //   totalBeds      = all rows with Room cell populated
    //   occupiedBeds   = bed rows with both Room and Name
    //   availableBeds  = bed rows with Room but no Name (open bed)
    // These are the source-of-truth headcount from the bedboard itself,
    // independent of how many residents we've linked to clinic clients.
    totalBeds: zod_1.z.number().int().nonnegative(),
    occupiedBeds: zod_1.z.number().int().nonnegative(),
    availableBeds: zod_1.z.number().int().nonnegative(),
    active: zod_1.z.boolean(),
    sortOrder: zod_1.z.number().int(),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
    // Number of HousingPlacement rows currently linked to clinic Clients.
    // Usually ≤ occupiedBeds (some residents may still be in the pending
    // links queue waiting for admin confirmation).
    currentOccupancy: zod_1.z.number().int().nonnegative().optional(),
});
exports.updateHousingLocationSchema = zod_1.z.object({
    displayName: zod_1.z.string().min(1).max(120).optional(),
    address: zod_1.z.string().optional(),
    city: zod_1.z.string().optional(),
    state: zod_1.z.string().optional(),
    zip: zod_1.z.string().optional(),
    houseManagerName: zod_1.z.string().optional(),
    houseManagerPhone: zod_1.z.string().optional(),
    wifiName: zod_1.z.string().optional(),
    wifiPassword: zod_1.z.string().optional(),
    doorCode: zod_1.z.string().optional(),
    secondaryDoorCode: zod_1.z.string().optional(),
    columnMap: exports.housingColumnMapSchema.optional(),
    columnMapSecondary: exports.housingColumnMapSchema.nullable().optional(),
    dataStartRow: zod_1.z.number().int().positive().optional(),
    active: zod_1.z.boolean().optional(),
    sortOrder: zod_1.z.number().int().optional(),
});
// ─── HousingPlacement ─────────────────────────────────────────────────────
exports.housingPlacementDtoSchema = zod_1.z.object({
    id: zod_1.z.string(),
    clientId: zod_1.z.string(),
    housingLocationId: zod_1.z.string(),
    locationCode: zod_1.z.string().optional(),
    locationDisplayName: zod_1.z.string().optional(),
    // Client convenience denorms for list views
    clientName: zod_1.z.string().optional(),
    cohortId: zod_1.z.string().optional(),
    roomNumber: zod_1.z.string(),
    lockerNumber: zod_1.z.string(),
    contactPhone: zod_1.z.string(),
    sourceProgramText: zod_1.z.string(),
    restrictionText: zod_1.z.string(),
    workStatus: zod_1.z.string(),
    notes: zod_1.z.string(),
    expectedMoveOutDate: zod_1.z.string().nullable(),
    startDate: zod_1.z.string(),
    endDate: zod_1.z.string().nullable(),
    isCurrent: zod_1.z.boolean(),
});
// ─── HousingSyncRun ───────────────────────────────────────────────────────
exports.housingSyncRunDtoSchema = zod_1.z.object({
    id: zod_1.z.string(),
    startedAt: zod_1.z.string(),
    completedAt: zod_1.z.string().nullable(),
    triggeredBy: zod_1.z.string(),
    status: zod_1.z.string(),
    tabsScanned: zod_1.z.number().int(),
    rowsSeen: zod_1.z.number().int(),
    placementsOpened: zod_1.z.number().int(),
    placementsClosed: zod_1.z.number().int(),
    placementsUpdated: zod_1.z.number().int(),
    pendingLinksCreated: zod_1.z.number().int(),
    programMismatches: zod_1.z.number().int(),
    writebacksAttempted: zod_1.z.number().int(),
    writebacksSucceeded: zod_1.z.number().int(),
    errorMessage: zod_1.z.string().nullable(),
    tabBreakdown: zod_1.z.any().nullable(),
});
// ─── HousingPendingLink ───────────────────────────────────────────────────
exports.housingPendingLinkDtoSchema = zod_1.z.object({
    id: zod_1.z.string(),
    housingLocationId: zod_1.z.string(),
    locationCode: zod_1.z.string().optional(),
    rowKey: zod_1.z.string(),
    rawName: zod_1.z.string(),
    rawPhone: zod_1.z.string(),
    rawRoom: zod_1.z.string(),
    rawProgram: zod_1.z.string(),
    rawMoveInDate: zod_1.z.string(),
    candidateClientIds: zod_1.z.array(zod_1.z.string()),
    status: zod_1.z.enum(['PENDING', 'LINKED', 'IGNORED', 'PROMOTED']),
    linkedClientId: zod_1.z.string().nullable(),
    firstSeenAt: zod_1.z.string(),
    lastSeenAt: zod_1.z.string(),
});
exports.confirmHousingLinkSchema = zod_1.z.object({
    clientId: zod_1.z.string().uuid(),
});
//# sourceMappingURL=housing.js.map